import { addNewSecondStageUnits } from "../lib/helpers/addNewSecondStageUnits/addNewSecondStageUnits"
import { addNewVote } from "../lib/helpers/addNewVote/addNewVote"
import { getCurrentNominationLabel } from "../lib/helpers/getCurrentNominationLabel/getCurrentNominationLabel"
import { getCurrentNominationNominees } from "../lib/helpers/getCurrentNominationNominees/getCurrentNominationNominees"
import { INominationsResponce, INominee, NominationsT, PostVoteT, SecondStageVotesT } from "./types"
import { combine, createEffect, createEvent, createStore, restore, sample } from "effector"
import { createGate } from "effector-react"
import { once } from "patronum"
import { messagesTypes, setMessage } from "src/entity/Messages"
import { $userHash, $userId } from "src/entity/User"
import { IPostSecondStageVotes } from "src/features/postSecondStageVotes"
import { api } from "src/shared/api/api"

export const PretendentsGate = createGate()
export const StageSecondGate = createGate()

const getPollItemsFx = createEffect<void, INominationsResponce[], Error>(api.getNominations)
const getSecondStageItemsFx = createEffect<void, INominationsResponce[], Error>(api.getSecondStagePretendents)
const pretendentsGateOpen = once(PretendentsGate.open)
const stageSecondGateOpen = once(StageSecondGate.open)

export const resetFirstStageVotes = createEvent()
export const setCurrentNomination = createEvent<NominationsT>()
export const $currentNominationName = createStore<NominationsT | null>(null)
export const $currentNominationLabel = createStore<string>("")
export const $pollItems = createStore<INominationsResponce[]>([])
export const $currentNomitionPretendents = createStore<INominee[] | null>(null)

export const setPretendents = createEvent<PostVoteT>()
const $newPretendents = restore(setPretendents, {})
export const $firstStageVotes = createStore<PostVoteT>({}).reset(resetFirstStageVotes)

export const setSecondStageVote = createEvent<SecondStageVotesT | null>()
export const setSecondStageLabel = createEvent<SecondStageVotesT | null>()
const $newSecondStageVote = restore(setSecondStageVote, null)
const $newSecondStageLabel = restore(setSecondStageLabel, null)
export const resetSecondStageVotes = createEvent()
export const $secondStageVotes = createStore<SecondStageVotesT | null>(null)
  .on(setSecondStageVote, (state, patch) => ({ ...state, ...patch }))
  .reset(resetSecondStageVotes)
export const $secondStageLabels = createStore<SecondStageVotesT | null>(null)
export const sendSecondStageVotes = createEvent()

const $auth = combine({
  vkId: $userId,
  userHash: $userHash,
})

export const accordionEvent = createEvent()
export const $accordionTap = createStore(false).on(accordionEvent, (state) => !state)

export const postSecondStageVotesFx = createEffect<
  { vkId: string; userHash: string; votes: SecondStageVotesT | null }, // вход в эффект из приложения
  unknown,
  Error
>(async ({ vkId, userHash, votes }) => {
  if (votes) {
    const payload: IPostSecondStageVotes = { vkId: vkId, userHash, votes }
    return api.postSecondStageVotes(payload)
  }
})

const fireConfettiFx = createEffect(() => {
  window.parent.postMessage(
    {
      type: "confettiFire",
    },
    "*"
  )
})

sample({
  clock: $newPretendents,
  source: { $newPretendents, $firstStageVotes },
  fn: addNewVote,
  target: $firstStageVotes,
})

// get first stage items
sample({
  clock: pretendentsGateOpen,
  target: getPollItemsFx,
})

sample({
  clock: getPollItemsFx.doneData,
  target: $pollItems,
})

// get second stage items
sample({
  clock: stageSecondGateOpen, // или openSecondStage
  target: getSecondStageItemsFx,
})

sample({
  clock: getSecondStageItemsFx.doneData,
  target: $pollItems,
})

sample({
  clock: setCurrentNomination,
  target: $currentNominationName,
})

sample({
  clock: $currentNominationName,
  source: { $currentNominationName, $pollItems },
  fn: getCurrentNominationLabel,
  target: $currentNominationLabel,
})

sample({
  clock: $currentNominationLabel,
  source: { $currentNominationName, $pollItems },
  fn: getCurrentNominationNominees,
  target: $currentNomitionPretendents,
})

sample({
  clock: $newSecondStageVote,
  source: {
    newSecondStageUnit: $newSecondStageVote,
    secondStageUnit: $secondStageVotes,
  },
  fn: addNewSecondStageUnits,
  target: $secondStageVotes,
})

sample({
  clock: $newSecondStageLabel,
  source: {
    newSecondStageUnit: $newSecondStageLabel,
    secondStageUnit: $secondStageLabels,
  },
  fn: addNewSecondStageUnits,
  target: $secondStageLabels,
})

sample({
  clock: resetSecondStageVotes,
  fn: () => null,
  target: [$secondStageVotes, $secondStageLabels],
})

sample({
  clock: sendSecondStageVotes,
  source: { auth: $auth, votes: $secondStageVotes },
  filter: ({ auth, votes }) => Boolean(auth.userHash && Object.keys(votes || {}).length),
  fn: ({ auth, votes }) => ({
    vkId: auth.vkId,
    userHash: auth.userHash as string,
    votes,
  }),
  target: postSecondStageVotesFx,
})

sample({
  clock: sendSecondStageVotes,
  source: $secondStageVotes,
  filter: (votes) => Object.keys(votes || {}).length === 0,
  fn: () => messagesTypes.FILL_ERROR,
  target: setMessage,
})

// нет auth → тоже ошибка
sample({
  clock: sendSecondStageVotes,
  source: $auth,
  filter: ({ userHash, vkId }) => !(userHash && String(vkId)),
  fn: () => messagesTypes.FILL_ERROR,
  target: setMessage,
})

// отправка упала
sample({
  clock: postSecondStageVotesFx.failData,
  fn: () => messagesTypes.POST_ERROR,
  // fn: () => messagesTypes.SET_CONGRAT_FORM,
  target: setMessage,
})

// успех
sample({
  clock: postSecondStageVotesFx.doneData,
  fn: () => messagesTypes.SET_CONGRAT_FORM,
  target: setMessage,
})

sample({
  clock: postSecondStageVotesFx.doneData,
  target: fireConfettiFx,
})

// (опционально) очистить выборы после успеха
sample({
  clock: postSecondStageVotesFx.done,
  target: resetSecondStageVotes,
})
